import React from "react";
import { OBJECT_TYPES, isPlayerLikeType, resolvePlayerScale } from "@/constants/pitchObjects";

// Small helper: soccer-ball style — white circle, central black pentagon
// with five short seam strokes and small dark hexagon patches at the ends
// so the ball reads like a real football at any size.
const SoccerBall = ({ s }) => {
  const rOuter = s;
  const rPent = s * 0.4;
  const start = -Math.PI / 2;
  const verts = Array.from({ length: 5 }, (_, i) => {
    const a = start + (i * 2 * Math.PI) / 5;
    return { x: Math.cos(a) * rPent, y: Math.sin(a) * rPent };
  });
  const pentPath = verts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ") + " Z";
  const bw = Math.max(1, s * 0.09);
  return (
    <g>
      <circle cx={0} cy={0} r={rOuter} fill="#FFFFFF" stroke="#0F172A" strokeWidth={bw * 1.2} />
      <path d={pentPath} fill="#0F172A" />
      {verts.map((p, i) => {
        const mag = Math.hypot(p.x, p.y) || 1;
        const ux = p.x / mag;
        const uy = p.y / mag;
        // seam line from pentagon vertex out to just short of the edge
        const seamEnd = { x: ux * (rOuter * 0.88), y: uy * (rOuter * 0.88) };
        // small patch near the edge (rotated square) reads as a hexagon
        const patchR = s * 0.24;
        const patchC = { x: ux * (rOuter * 0.78), y: uy * (rOuter * 0.78) };
        const angDeg = (Math.atan2(uy, ux) * 180) / Math.PI + 45;
        return (
          <g key={i}>
            <line
              x1={p.x}
              y1={p.y}
              x2={seamEnd.x}
              y2={seamEnd.y}
              stroke="#0F172A"
              strokeWidth={bw}
              strokeLinecap="round"
            />
            <rect
              x={-patchR / 2}
              y={-patchR / 2}
              width={patchR}
              height={patchR}
              transform={`translate(${patchC.x} ${patchC.y}) rotate(${angDeg})`}
              fill="#0F172A"
            />
          </g>
        );
      })}
    </g>
  );
};

// Low-profile soccer training disc cone/marker — matches a real coaching
// "mini disc" (Kwik Goal style): a very shallow dome rising smoothly from
// a wide flat base with a small recessed hole in the crown. Silhouette is
// ~2.85× wider than tall, ratio unchanged when rotated. `fill` is the
// current cone colour so per-instance colour behaviour continues to work.
const TrainingCone = ({ s, fill }) => {
  const W1 = s * 1.0; // base half-width
  const Hb = s * 0.35; // base rim y (below centre)
  const Hdome = s * 0.55; // dome vertical radius above the base rim
  const ryB = s * 0.15; // base ellipse vertical radius (thin oval footprint)
  const holeRx = s * 0.22;
  const holeRy = s * 0.07;
  const holeCy = -s * 0.12;
  const bw = Math.max(1, s * 0.06);

  // Outer silhouette: shallow dome arc across the top → base front arc
  // along the bottom, closing back to the start.
  const silhouette =
    `M ${-W1} ${Hb} ` +
    `A ${W1} ${Hdome} 0 0 1 ${W1} ${Hb} ` +
    `A ${W1} ${ryB} 0 0 1 ${-W1} ${Hb} ` +
    `Z`;

  return (
    <g>
      <path
        d={silhouette}
        fill={fill}
        stroke="#0F172A"
        strokeOpacity={0.32}
        strokeWidth={bw}
        strokeLinejoin="round"
      />
      {/* Soft top highlight — thin lighter arc just under the crown to
         reinforce the domed (not flat) silhouette. */}
      <path
        d={`M ${-W1 * 0.55} ${-s * 0.16} A ${W1 * 0.55} ${s * 0.28} 0 0 1 ${W1 * 0.55} ${-s * 0.16}`}
        fill="none"
        stroke="#FFFFFF"
        strokeOpacity={0.35}
        strokeWidth={bw * 0.9}
        strokeLinecap="round"
      />
      {/* Central recessed hole — small dark ellipse at the crown. */}
      <ellipse
        cx={0}
        cy={holeCy}
        rx={holeRx}
        ry={holeRy}
        fill="#0F172A"
        fillOpacity={0.55}
      />
    </g>
  );
};

// Soccer training mannequin — inspired by Kwik Goal Coerver-style panels:
// flat rectangular body with rounded shoulders, an oval loop handle at the
// top, two long narrow support legs, and a wide flat base. Silhouette is
// tall and slim (~2.5× taller than wide) so it reads immediately as
// training equipment even at small canvas sizes.
const TrainingMannequin = ({ s, fill }) => {
  const bw = Math.max(1, s * 0.06);
  // Body panel
  const bodyW = 1.2 * s;
  const bodyH = 1.9 * s;
  const bodyTop = -1.4 * s;
  const bodyBottom = bodyTop + bodyH; // 0.5s
  const shoulderR = 0.28 * s;
  // Legs
  const legTop = bodyBottom;
  const legBottom = 1.75 * s;
  const legW = 0.14 * s;
  const legOffset = 0.42 * s;
  // Base bar
  const baseTop = legBottom;
  const baseBottom = 1.95 * s;
  const baseW = 1.6 * s;
  // Handle loop
  const handleCy = -1.75 * s;
  const handleRx = 0.25 * s;
  const handleRy = 0.28 * s;
  const handleStroke = 0.12 * s;
  // Neck (bridge between loop and body)
  const neckW = 0.22 * s;
  const neckX = -neckW / 2;
  const neckTop = handleCy + handleRy - 0.02 * s;
  const neckBottom = bodyTop + 0.05 * s;

  const outline = "#0F172A";
  const outlineOpacity = 0.35;

  return (
    <g>
      {/* Wide base bar */}
      <rect
        x={-baseW / 2}
        y={baseTop}
        width={baseW}
        height={baseBottom - baseTop}
        rx={0.04 * s}
        fill={fill}
        stroke={outline}
        strokeOpacity={outlineOpacity}
        strokeWidth={bw}
      />
      {/* Two support legs */}
      <rect
        x={-legOffset - legW / 2}
        y={legTop}
        width={legW}
        height={legBottom - legTop}
        fill={fill}
        stroke={outline}
        strokeOpacity={outlineOpacity}
        strokeWidth={bw * 0.9}
      />
      <rect
        x={legOffset - legW / 2}
        y={legTop}
        width={legW}
        height={legBottom - legTop}
        fill={fill}
        stroke={outline}
        strokeOpacity={outlineOpacity}
        strokeWidth={bw * 0.9}
      />
      {/* Neck bridge (drawn under body so its bottom is hidden) */}
      <rect
        x={neckX}
        y={neckTop}
        width={neckW}
        height={neckBottom - neckTop}
        fill={fill}
        stroke={outline}
        strokeOpacity={outlineOpacity}
        strokeWidth={bw * 0.8}
      />
      {/* Rectangular body with rounded shoulders */}
      <rect
        x={-bodyW / 2}
        y={bodyTop}
        width={bodyW}
        height={bodyH}
        rx={shoulderR}
        fill={fill}
        stroke={outline}
        strokeOpacity={outlineOpacity + 0.05}
        strokeWidth={bw}
      />
      {/* Subtle perforation hint — light dots across the body panel. */}
      {[-1.05, -0.72, -0.39, -0.06, 0.27].map((row, i) => (
        <g key={i}>
          {[-0.4, -0.2, 0, 0.2, 0.4].map((col, j) => (
            <circle
              key={j}
              cx={col * s}
              cy={row * s}
              r={s * 0.028}
              fill={outline}
              fillOpacity={0.22}
            />
          ))}
        </g>
      ))}
      {/* Handle loop — open ring */}
      <ellipse
        cx={0}
        cy={handleCy}
        rx={handleRx}
        ry={handleRy}
        fill="none"
        stroke={fill}
        strokeWidth={handleStroke}
        strokeLinejoin="round"
      />
      {/* Thin outline over the loop for legibility on the pitch */}
      <ellipse
        cx={0}
        cy={handleCy}
        rx={handleRx + handleStroke / 2}
        ry={handleRy + handleStroke / 2}
        fill="none"
        stroke={outline}
        strokeOpacity={outlineOpacity}
        strokeWidth={bw * 0.5}
      />
      <ellipse
        cx={0}
        cy={handleCy}
        rx={handleRx - handleStroke / 2}
        ry={handleRy - handleStroke / 2}
        fill="none"
        stroke={outline}
        strokeOpacity={outlineOpacity}
        strokeWidth={bw * 0.5}
      />
    </g>
  );
};

// KwikGoal-style mannequin (legacy vertical-pole variant retained for
// backwards-compat if referenced elsewhere).
const KwikGoalMannequin = ({ s, fill, text, label }) => {
  const base = s * 1.4;
  const bodyH = s * 1.9;
  const bodyW = s * 1.05;
  const headR = s * 0.5;
  return (
    <g>
      {/* Base (heavy, rubber-like) */}
      <ellipse cx={0} cy={bodyH * 0.55} rx={base * 0.55} ry={s * 0.22} fill="#0F172A" />
      <ellipse cx={0} cy={bodyH * 0.52} rx={base * 0.55} ry={s * 0.14} fill={fill} />
      {/* Vertical pole */}
      <rect x={-s * 0.14} y={-bodyH * 0.35} width={s * 0.28} height={bodyH * 0.9} fill="#334155" />
      {/* Body (torso) */}
      <rect
        x={-bodyW / 2}
        y={-bodyH * 0.35}
        width={bodyW}
        height={bodyH * 0.65}
        rx={bodyW * 0.35}
        fill={fill}
        stroke="#FFFFFF"
        strokeWidth={Math.max(1, s * 0.08)}
      />
      {/* Head */}
      <circle
        cx={0}
        cy={-bodyH * 0.55}
        r={headR}
        fill={fill}
        stroke="#FFFFFF"
        strokeWidth={Math.max(1, s * 0.08)}
      />
      {label && (
        <text
          x={0}
          y={0}
          dy="0.35em"
          textAnchor="middle"
          fill={text}
          fontSize={s * 0.5}
          fontWeight="700"
          fontFamily="Manrope, sans-serif"
        >
          {label}
        </text>
      )}
    </g>
  );
};

// Renders a single draggable object. Position + rotation are applied by the
// parent via a group transform; sizeScale scales the whole visual.
// When showHandles is true (Select tool + selected), resize + rotate handles
// are rendered outside the rotation group so they can be grabbed regardless
// of the object's current rotation.
export const PitchObject = ({ obj, isSelected, showHandles, allowResize = true, pitchRotation = 0, onPointerDown, onSelect, onHandlePointerDown }) => {
  const def = OBJECT_TYPES[obj.type];
  const isPlayerLike = isPlayerLikeType(obj.type);
  // Player-like objects use the Small/Medium/Large preset system:
  //   size = "small" | "medium" | "large" → numeric scale via resolvePlayerScale.
  // Legacy numeric player sizes (from before the preset system) and
  // missing values both resolve to "medium" so historical activities
  // load with a stable appearance. Non-player objects continue to use
  // a numeric size scale (their resize handle is still active).
  const sizeScale = isPlayerLike
    ? resolvePlayerScale(obj.size)
    : (typeof obj.size === "number" ? obj.size : 1);
  const rotation = typeof obj.rotation === "number" ? obj.rotation : 0;
  const s = def.size * sizeScale;
  // Player-only display mode: number | initials | position | blank.
  // Number/Initials/Position are stored independently so switching modes
  // only changes which one is shown — editing one never overwrites another.
  const displayMode = isPlayerLike && obj.displayMode ? obj.displayMode : null;
  let label;
  if (isPlayerLike && displayMode === "blank") {
    label = "";
  } else if (isPlayerLike && displayMode === "initials") {
    label = (obj.initials || "").slice(0, 3);
  } else if (isPlayerLike && displayMode === "position") {
    label = (obj.position || "").slice(0, 3);
  } else {
    // NUMBER mode or legacy (no mode set) → existing label / number.
    label = obj.label ?? def.shortLabel;
  }

  // Per-instance colour override: players/defenders/neutrals/goalkeepers can
  // override the type's default fill. Text stays readable by auto-picking
  // black or white based on the effective fill's luminance.
  const effectiveFill = isPlayerLike && obj.color ? obj.color : def.fill;
  const readableTextColor = (() => {
    if (!isPlayerLike || !obj.color) return def.text;
    const hex = obj.color.replace("#", "");
    if (hex.length !== 6) return def.text;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    // Perceived luminance (sRGB approximation)
    const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return L > 0.6 ? "#0F172A" : "#FFFFFF";
  })();

  const commonProps = {
    onPointerDown: (e) => {
      e.stopPropagation();
      // Selection is handled inside Pitch.jsx so it can inspect e.shiftKey
      // and route to single-select vs multi-select toggle.
      onPointerDown(e, obj.id);
    },
    style: { cursor: "grab", touchAction: "none" },
    "data-testid": `object-${obj.type}-${obj.id}`,
  };

  const selectionRing = isSelected ? (
    <circle
      cx={0}
      cy={0}
      r={s * 1.35 + 6}
      fill="none"
      stroke="#10B981"
      strokeWidth={2.5}
      strokeDasharray="4 3"
    />
  ) : null;

  let shape = null;
  if (def.shape === "circle") {
    shape = (
      <>
        <circle cx={0} cy={0} r={s} fill={effectiveFill} stroke="#FFFFFF" strokeWidth={Math.max(1.5, s * 0.08)} />
        {label && (
          <text
            x={0}
            y={0}
            dy="0.35em"
            textAnchor="middle"
            fill={readableTextColor}
            fontSize={label.length >= 3 ? s * 0.55 : label.length === 2 ? s * 0.7 : s * 0.85}
            fontWeight="700"
            fontFamily="Manrope, sans-serif"
          >
            {label}
          </text>
        )}
      </>
    );
  } else if (def.shape === "ball") {
    shape = <SoccerBall s={s} />;
  } else if (def.shape === "triangle") {
    shape = (
      <>
        <TrainingCone s={s} fill={def.fill} />
        {label && (
          <text
            x={0}
            y={s * 0.9}
            textAnchor="middle"
            fill={def.text}
            fontSize={s * 0.55}
            fontWeight="700"
            fontFamily="Manrope, sans-serif"
          >
            {label}
          </text>
        )}
      </>
    );
  } else if (def.shape === "goal") {
    const w = s;
    const h = s * 0.35;
    // Goals sit ON the goal line, so their long axis must run parallel
    // to that line. Since PitchObject renders in view coords (not inside
    // the pitch-rotation group), we apply the pitch rotation here so the
    // goal rotates 90° when the pitch is drawn horizontally. The label
    // counter-rotates so it stays upright for the reader.
    shape = (
      <g transform={`rotate(${pitchRotation})`}>
        <rect
          x={-w}
          y={-h / 2}
          width={w * 2}
          height={h}
          fill={def.fill}
          stroke="#FFFFFF"
          strokeWidth={Math.max(1.5, s * 0.08)}
          rx={2}
        />
        {label && (
          <g transform={`rotate(${-pitchRotation})`}>
            <text
              x={0}
              y={0}
              dy="0.35em"
              textAnchor="middle"
              fill={def.text}
              fontSize={h * 0.75}
              fontWeight="700"
              fontFamily="Manrope, sans-serif"
            >
              {label}
            </text>
          </g>
        )}
      </g>
    );
  } else if (def.shape === "mannequin") {
    shape = <TrainingMannequin s={s} fill={def.fill} />;
  }

  const handleRadius = Math.max(6, s * 0.22);
  const ringR = s * 1.35 + 6;
  const handleDistance = ringR + 12;

  return (
    <g transform={`translate(${obj.x} ${obj.y})`} {...commonProps}>
      {selectionRing}
      <g transform={`rotate(${rotation})`}>{shape}</g>
      {showHandles && (
        <g style={{ cursor: "pointer" }} onPointerDown={(e) => e.stopPropagation()}>
          {/* Resize handle at bottom-right — hidden for player-like
              objects which use the Small/Medium/Large size preset
              system in the InfoPanel instead of free-form resize. */}
          {allowResize && (
            <circle
              cx={handleDistance * 0.707}
              cy={handleDistance * 0.707}
              r={handleRadius}
              fill="#10B981"
              stroke="#FFFFFF"
              strokeWidth={2}
              data-testid={`object-resize-handle-${obj.id}`}
              onPointerDown={(e) => {
                e.stopPropagation();
                onHandlePointerDown(e, "resize", obj.id);
              }}
              style={{ cursor: "nwse-resize" }}
            />
          )}
          {/* Rotation handle above */}
          <line
            x1={0}
            y1={-ringR}
            x2={0}
            y2={-handleDistance - handleRadius}
            stroke="#10B981"
            strokeWidth={2}
          />
          <circle
            cx={0}
            cy={-handleDistance - handleRadius}
            r={handleRadius}
            fill="#10B981"
            stroke="#FFFFFF"
            strokeWidth={2}
            data-testid={`object-rotate-handle-${obj.id}`}
            onPointerDown={(e) => {
              e.stopPropagation();
              onHandlePointerDown(e, "rotate", obj.id);
            }}
            style={{ cursor: "grab" }}
          />
        </g>
      )}
    </g>
  );
};
